import { ProductDataItem } from '../../orderhistory/ProductDataItem';
import { DocumentInformation, DocumentFileLink } from '../../orderhistory/DocumentInformation';
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ArrowLeft, Receipt, Percent, Wallet } from 'lucide-react';
import { iconBgBlue, iconBgAmber, iconBgGreen } from '../../../styling/stylingHelper';
import { useOrderHistory } from '../../../stores/appOrderHistoryStore';
import { formatDateLong, hasAnyInvoiceLogo } from '../../../services/helperService';
import { useTranslation } from 'react-i18next';
import { ALL_RESTAURANTS_ID, useAppUserDefaultStore } from '../../../stores/appUserDefaultStore';
import { EmailProductData } from '../../../stores/appOrderHistoryStore';
import { GmailIdCopy } from './GmailCopy';
import { Platform } from '../../../types/Platforms';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { calculateProductTotal } from '../../../utils/platformUtils';
import AppFooter from '../../common/AppFooter';
import { StatCard } from '../../ui/StatCard';
import { SupplierLogo } from '../../ui/SupplierLogo';
import { getOrderVatAmount, getOrderVatRate } from '../../orderhistory/vatUtils';
import { useAuth } from '../../../services/authentication/authContext';
import { hasDashboardFoodOnly, canEditQuantityInfoOverride } from '../../../services/accessManagement/accessManagement';
import QuantityInfoEditModal, { QuantityInfoOverridePayload } from '../../orderhistory/QuantityInfoEditModal';
import { updateProductQuantityInfo, resetProductQuantityInfo, previewProductQuantityChange } from '../../../services/orderhistory/orderhistoryService';
import { refreshAfterCustomerRequestApproval } from '../../../services/customerRequests/refreshAfterApproval';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';

const OrderHistoryDetailPage = () => {
  const { t } = useTranslation();
  const { backendUser, accountSettings } = useAuth();

  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const load = useOrderHistory(s => s.load);
  const order = useOrderHistory(s => s.byId(orderId ?? ''));
  const restaurantId = useAppUserDefaultStore(s => s.restaurant?.id);
  const platformToSectionIndex = useRestaurantStore(s => s.platformToSectionIndex);

  // Show debug info only for admin users with dashboard_food_only setting
  const showDebugInfo = backendUser?.role === 'admin' && hasDashboardFoodOnly(backendUser, accountSettings);

  // Whether the user may correct parsed quantity info (admin-only account setting)
  const canEditQuantityInfo = canEditQuantityInfoOverride(backendUser, accountSettings);
  const [editingProduct, setEditingProduct] = useState<EmailProductData | null>(null);

  useEffect(() => {
    if (!orderId || !restaurantId) return;
    load(restaurantId, platformToSectionIndex);
  }, [load, restaurantId, orderId, platformToSectionIndex]);

  useEffect(() => {
    const scroller = document.getElementById('mainContainer') as HTMLDivElement | null;
    if (!scroller) return;
    scroller.scrollTop = 0;
  }, []);

  useEffect(() => {
    // Order dump for debugging invoice parsing — local dev only, never in a deployed build.
    if (process.env.NODE_ENV !== 'development') return;
    console.log('order', JSON.stringify(order, null, 2));
  }, [order]);

  if (!order) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted dark:text-muted-dark">Loading…</p>
      </div>
    );
  }

  const formattedDate = formatDateLong(new Date(order.orderDate));

  // Sort products by total spend (quantity * price)
  const sortedProducts = [...(order.productData || [])].sort((a, b) => {
    const totalA = (a.quantity || 0) * (a.price || 0);
    const totalB = (b.quantity || 0) * (b.price || 0);
    return totalB - totalA;
  });

  // Calculate sum of all product totals (ex-VAT) for debug comparison
  const productSumTotal = (order.productData || []).reduce(
    (sum, product) => sum + calculateProductTotal(product, order.platform), 0
  );

  const handleGoBack = () => {
    navigate(-1);
  };

  // Force a refetch so the edited quantity info is reflected on the page.
  const reloadOrder = async () => {
    if (!restaurantId) return;
    useOrderHistory.setState({ currentRestaurantId: null });
    await load(restaurantId, platformToSectionIndex);
  };

  // The change also rewrites recipes, so loaded dishes and preparations have to catch up. This page reloads its own orders.
  const refreshAfterQuantityChange = async () => {
    await reloadOrder();
    await refreshAfterCustomerRequestApproval(
      { type: 'product_quantity', restaurant: { id: order.restaurantId ?? restaurantId ?? '', name: '' }, actionResult: null },
      { keepOrders: true }
    );
  };

  const handlePreviewQuantityChange = async (payload: QuantityInfoOverridePayload | null) => {
    if (!editingProduct) throw new Error('No product selected');
    return previewProductQuantityChange(order.restaurantPlatformId, order.platform, editingProduct.articleId, editingProduct.name, payload);
  };

  const handleSaveQuantityInfo = async (payload: QuantityInfoOverridePayload, previewFingerprint: string) => {
    if (!editingProduct) return;
    await updateProductQuantityInfo(
      order.restaurantPlatformId,
      order.platform,
      editingProduct.articleId,
      editingProduct.name,
      payload,
      previewFingerprint
    );
    await refreshAfterQuantityChange();
  };

  const handleResetQuantityInfo = async (previewFingerprint: string) => {
    if (!editingProduct) return;
    await resetProductQuantityInfo(
      order.restaurantPlatformId,
      order.platform,
      editingProduct.articleId,
      editingProduct.name,
      previewFingerprint
    );
    await refreshAfterQuantityChange();
  };

  return (
    <div>
      <MobilePageHeader title={t('orderHistory.orderDetails')} />
      {/* Header */}
      <div className="flex items-center mb-4">
        <ArrowLeft className="w-6 h-6 mt-1 mr-2 cursor-pointer" onClick={handleGoBack} />
        <h1 className="text-2xl font-semibold text-text dark:text-text-dark hidden lg:block">
          {t('orderHistory.orderDetails')}
        </h1>
      </div>

      {/* Content sections with consistent spacing */}
      <div className="mt-4 mb-4 flex flex-col gap-6">
        {/* Order Header - Platform, Date, Client */}
        <div className="w-full">
          <div className="bg-card dark:bg-card-dark rounded-xl p-6">
            <div className="flex items-center justify-center gap-3 flex-wrap">
              {/* Show logo if available, otherwise show supplier name as text for any_invoice */}
              {order.platform === Platform.ANY_INVOICE && order.unknownSupplierName && !hasAnyInvoiceLogo(order.unknownSupplierName) ? (
                <span className="text-2xl font-bold text-text dark:text-text-dark">{order.unknownSupplierName}</span>
              ) : (
                <SupplierLogo
                  platform={order.platform}
                  unknownSupplierName={order.unknownSupplierName}
                  size="large"
                  className="inline-block"
                  alt={order.platform}
                />
              )}
              <span className="text-2xl font-bold text-text dark:text-text-dark">•</span>
              <span className="text-2xl font-bold text-text dark:text-text-dark">{formattedDate}</span>
              <span className="text-2xl font-bold text-text dark:text-text-dark">•</span>
              <span className="text-2xl font-bold text-text dark:text-text-dark">{order.clientName || 'Unknown Client'}</span>
            </div>
          </div>
        </div>

        {/* Financial Summary - Simple Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Total Excl. VAT */}
          <StatCard
            icon={<Receipt className="w-6 h-6" style={{ color: '#2E90FA' }} />}
            iconBg={iconBgBlue}
            title={t('orderHistory.total')}
            value={order.total}
            valueType="currency"
          />

          {/* VAT Amount */}
          {order.totalWithTaxes !== undefined && (
            <StatCard
              icon={<Percent className="w-6 h-6" style={{ color: '#F59E0B' }} />}
              iconBg={iconBgAmber}
              title={t('orderHistory.vat', { defaultValue: 'VAT' })}
              value={getOrderVatAmount(order.platform, order.totalWithTaxes ?? 0, order.total ?? 0, order.shipping ?? 0)}
              valueType="currency"
            />
          )}

          {/* Total with Taxes */}
          {order.totalWithTaxes !== undefined && (
            <StatCard
              icon={<Wallet className="w-6 h-6" style={{ color: '#079455' }} />}
              iconBg={iconBgGreen}
              title={t('orderHistory.totalWithTaxes')}
              value={order.totalWithTaxes}
              valueType="currency"
            />
          )}
        </div>

        {/* Order Metadata */}
        <DocumentInformation title={t('orderHistory.orderInformation', { defaultValue: 'Order Information' })}>
              <div className="flex gap-3">
                <span className="font-medium text-muted dark:text-muted-dark min-w-[120px]">
                  {t('orderHistory.platform')}:
                </span>
                <span className="text-text dark:text-text-dark capitalize font-semibold">
                  {order.platform === Platform.ANY_INVOICE && order.unknownSupplierName
                    ? order.unknownSupplierName
                    : order.platform}
                </span>
              </div>
              <div className="flex gap-3">
                <span className="font-medium text-muted dark:text-muted-dark min-w-[120px]">
                  {t('orderHistory.orderNumber')}:
                </span>
                <span className="text-text dark:text-text-dark font-mono font-semibold">
                  {order.orderNumber}
                </span>
              </div>
              <div className="hidden sm:flex gap-3 items-center">
                <span className="font-medium text-muted dark:text-muted-dark min-w-[120px]">
                  Email:
                </span>
                <GmailIdCopy gmailId={order.gmailId} />
              </div>
              {(() => {
                // Try to get the tax rate from order data, or calculate it
                const taxRate = order.taxRate ?? (
                  order.totalWithTaxes !== undefined
                    ? getOrderVatRate(order.platform, order.totalWithTaxes, order.total, order.shipping ?? 0, order.productData)
                    : undefined
                );

                return taxRate !== undefined ? (
                  <div className="flex gap-3">
                    <span className="font-medium text-muted dark:text-muted-dark min-w-[120px]">
                      {t('orderHistory.vatRate', { defaultValue: 'VAT Rate' })}:
                    </span>
                    <span className="text-text dark:text-text-dark font-semibold">
                      {(taxRate * 100).toFixed(0)}%
                    </span>
                  </div>
                ) : null;
              })()}
              {order.sourceFileUrl && (
                <div className="hidden sm:flex gap-3">
                  <span className="font-medium text-muted dark:text-muted-dark min-w-[120px]">
                    {t('orderHistory.sourceFile')}:
                  </span>
                  <DocumentFileLink href={order.sourceFileUrl} />
                </div>
              )}
              {order.factuurSourceFileUrl && order.factuurSourceFileUrl !== order.sourceFileUrl && (
                <div className="hidden sm:flex gap-3">
                  <span className="font-medium text-muted dark:text-muted-dark min-w-[120px]">
                    {t('orderHistory.factuurFile')}:
                  </span>
                  <DocumentFileLink href={order.factuurSourceFileUrl} />
                </div>
              )}
              {order.discount !== undefined && order.discount > 0 && (
                <div className="flex gap-3">
                  <span className="font-medium text-muted dark:text-muted-dark min-w-[120px]">
                    {t('orderHistory.discount')}:
                  </span>
                  <span className="text-text dark:text-text-dark font-semibold">
                    -€{order.discount.toFixed(2)}
                  </span>
                </div>
              )}
              {order.shipping !== undefined && order.shipping > 0 && (
                <div className="flex gap-3">
                  <span className="font-medium text-muted dark:text-muted-dark min-w-[120px]">
                    {t('orderHistory.shipping')}:
                  </span>
                  <span className="text-text dark:text-text-dark font-semibold">
                    €{order.shipping.toFixed(2)}
                  </span>
                </div>
              )}
        </DocumentInformation>

        {/* Products Section */}
        <div className="w-full">
          <div className="bg-card dark:bg-card-dark rounded-xl p-4">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-text dark:text-text-dark">
                {t('orderHistory.youOrdered', { count: sortedProducts.length })}
              </h2>
              {showDebugInfo && (
                <span className="text-sm font-medium text-muted dark:text-muted-dark">
                  Product sum: €{productSumTotal.toFixed(2)}
                </span>
              )}
            </div>

            {/* Products list */}
            <div>
              {sortedProducts.length > 0 ? (
                <div className="space-y-2">
                  {sortedProducts.map((p, index) => (
                    <ProductDataItem
                      key={`${p.articleId || 'product'}-${index}`}
                      product={p}
                      platform={order.platform}
                      canEdit={canEditQuantityInfo}
                      onQuantityClick={setEditingProduct}
                    />
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-muted dark:text-muted-dark">
                  {t('orderHistory.noProductsFound')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {editingProduct && (
        <QuantityInfoEditModal
          requestSeed={{ restaurantId: order.restaurantId ?? (restaurantId !== ALL_RESTAURANTS_ID ? restaurantId : undefined), source: { kind: 'order', id: order.id } }}
          productName={editingProduct.name || 'Unknown Product'}
          rawQuantity={editingProduct.rawQuantity || editingProduct.quantity}
          category={editingProduct.category}
          currentQuantityInfo={editingProduct.quantityInfo}
          isOverridden={'originalQuantityInfo' in editingProduct}
          onPreview={handlePreviewQuantityChange}
          onSave={handleSaveQuantityInfo}
          onReset={handleResetQuantityInfo}
          onClose={() => setEditingProduct(null)}
        />
      )}

      <AppFooter />
    </div>
  );
};

export default OrderHistoryDetailPage;